import * as React from "react";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "../../lib/utils";

const SelectContext = React.createContext({
  open: false,
  setOpen: () => {},
  value: undefined,
  onValueChange: () => {},
  registerItem: () => {},
  triggerRef: { current: null },
  contentRef: { current: null },
});

const Select = ({ value, defaultValue, onValueChange, children, disabled }) => {
  const [uncontrolled, setUncontrolled] = React.useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : uncontrolled;
  const [open, setOpen] = React.useState(false);
  const [labels, setLabels] = React.useState({});
  const triggerRef = React.useRef(null);
  const contentRef = React.useRef(null);

  const registerItem = React.useCallback((itemValue, label) => {
    setLabels((prev) => {
      if (prev[itemValue] === label) return prev;
      return { ...prev, [itemValue]: label };
    });
  }, []);

  const setValue = React.useCallback(
    (next) => {
      if (!isControlled) setUncontrolled(next);
      if (onValueChange) onValueChange(next);
      setOpen(false);
    },
    [isControlled, onValueChange]
  );

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <SelectContext.Provider
      value={{
        open,
        setOpen,
        value: current,
        onValueChange: setValue,
        registerItem,
        labels,
        disabled,
        triggerRef,
        contentRef,
      }}
    >
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
};

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  const { open, setOpen, triggerRef, disabled } = React.useContext(SelectContext);
  const mergedRef = (node) => {
    triggerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };
  return (
    <button
      type="button"
      ref={mergedRef}
      disabled={disabled}
      onClick={() => setOpen(!open)}
      className={cn(
        "ui-field flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background/60 px-3.5 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
    </button>
  );
});
SelectTrigger.displayName = "SelectTrigger";

const SelectValue = React.forwardRef(({ className, placeholder, children, ...props }, ref) => {
  const { value, labels } = React.useContext(SelectContext);
  // Prefer explicit children (consumer-controlled label), then the registered
  // SelectItem label for the current value. We deliberately do NOT fall back
  // to the raw `value`: if a value is set but the matching SelectItem hasn't
  // mounted yet (e.g. options still loading, or SelectContent not yet opened),
  // showing the raw id (often a UUID) is worse UX than showing the placeholder.
  let label;
  if (children !== undefined && children !== null && children !== false) {
    label = children;
  } else if (value !== undefined && value !== "") {
    label = labels?.[value];
  }
  return (
    <span ref={ref} className={cn("text-sm truncate", className)} {...props}>
      {label ?? <span className="text-muted-foreground">{placeholder}</span>}
    </span>
  );
});
SelectValue.displayName = "SelectValue";

const SelectContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const { open, contentRef } = React.useContext(SelectContext);
  const mergedRef = (node) => {
    contentRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };
  // Always mount children so each SelectItem's registerItem effect runs as
  // soon as the Select is in the tree, not only after the dropdown is opened.
  // This avoids a frame where SelectValue can't find a label for the current
  // value and would otherwise flash the raw id. We toggle visibility instead.
  return (
    <div
      ref={mergedRef}
      hidden={!open}
      className={cn(
        "ui-pop absolute left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto rounded-xl border bg-popover/95 p-1 text-popover-foreground shadow-md",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(({ className, value, children, ...props }, ref) => {
  const { value: selected, onValueChange, registerItem } = React.useContext(SelectContext);
  // useLayoutEffect (not useEffect) so the label is registered before the
  // browser's first paint after mount. Otherwise SelectValue would briefly
  // render the placeholder for one frame before the label appears.
  React.useLayoutEffect(() => {
    const flatten = (node) => {
      if (typeof node === "string" || typeof node === "number") return String(node);
      if (Array.isArray(node)) {
        const parts = node.map(flatten);
        return parts.every((p) => p !== null) ? parts.join("") : null;
      }
      return null;
    };
    const label = flatten(children);
    registerItem(value, label !== null && label !== "" ? label : value);
  }, [value, children, registerItem]);
  const isSelected = selected === value;
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={isSelected}
      onClick={() => onValueChange(value)}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-lg py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
        isSelected && "bg-accent/50",
        className
      )}
      {...props}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4" />
        </span>
      )}
      {children}
    </div>
  );
});
SelectItem.displayName = "SelectItem";

export {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
};
